import React, { useMemo } from "react";
import { useField } from "react-final-form";
import _ from "lodash";
import { Dropdown, DropdownProps } from "~/components/dropdown";

type EnumOption<E extends string> = {
  name: E;
  label: string;
};

export interface EnumDropdownProps<E extends string> {
  label: string;
  formField: string;
  helperText: string;
  required?: boolean;
  options: Record<E, string>;
  disabled?: boolean;
}

function EnumDropdownFormField<E extends string>(
  props: EnumDropdownProps<E>
): ReturnType<React.FC> {
  const {
    label,
    formField,
    helperText,
    required = false,
    options,
    disabled = false,
  } = props;

  const checkRequired = (value: string) =>
    value || !required ? undefined : `Please provide a valid ${label}`;

  const {
    meta: { invalid, error, submitError, touched },
    input: { value, onChange, onBlur },
  } = useField(formField, { validate: checkRequired });

  const hasError = (error || invalid || submitError) && touched;

  const items = useMemo(
    () =>
      _.orderBy(
        (Object.entries(options) as [E, string][]).map(
          ([name, optionLabel]) => ({ name, label: optionLabel })
        ),
        (x) => x.name
      ),
    [options]
  );
  const selectedItem = items.find((x) => x.name === value) || null;

  const dropdownProps: DropdownProps<EnumOption<E>> = {
    items: items,
    formatItem: (x) => x.label,
    formatSelection: (x) => x?.label || "",
    onSelected: (x) => onChange(x?.name || null),
    selectedItem,
    label,
    helperText: hasError ? error || submitError : helperText,
    disabled,
    required,
    isValid: !hasError,
    nullDisplay: "---",
    onBlur,
    name: "",
  };

  return <Dropdown {...dropdownProps} />;
}

export default EnumDropdownFormField;
